import * as XLSX from "xlsx";

// The inventory fields a sheet column can be mapped to, per item type. `key` is the
// target sent to the backend; `labelKey` points into Translations. `aliases` are
// extra header spellings the auto-match accepts on top of the key and the label.
//
// Backend contract (POST /api/v1/inventory/excel-upload):
// - `columnMappings` is `{ "<Excel header>": "<target>" }` -- the opposite direction
//   from the task/account uploads. The mapping UI keeps `{ fieldKey: header }` and
//   `toBackendColumnMappings` flips it on the way out.
// - The serial (main) column is detected from `itemType` by header name, so it only
//   has to be mapped when the sheet names it something else.
// - Two sheet columns may not end up on the same target.
const FIELD_DEFS = {
  identifier: { labelKey: "inventoryMeterNumber", aliases: [] },
  meterMake: { labelKey: "inventoryAddMeterMake", aliases: ["make", "manufacturer"] },
  meterType: { labelKey: "inventoryAddMeterType", aliases: ["type"] },
  meterComponents: { labelKey: "inventoryAddComponents", aliases: ["components"] },
  manufacturingYear: {
    labelKey: "inventoryAddManufacturingYear",
    aliases: ["year", "mfg year", "manufacture year"],
  },
  batchNo: { labelKey: "inventoryAddBatchNo", aliases: ["batch", "batch number"] },
  simNo: { labelKey: "inventoryAddSimNo", aliases: ["sim", "sim number"] },
  iccid: { labelKey: "inventoryAddIccid", aliases: ["iccid"] },
  // Seal sheets carry the range instead of a serial; the backend also reads the
  // "Seal Prefix / Seal From / Seal To" spellings.
  sealPrefix: { labelKey: "inventorySealPrefix", aliases: ["prefix", "seal prefix"] },
  sealFrom: { labelKey: "inventorySealFrom", aliases: ["from", "seal from"] },
  sealTo: { labelKey: "inventorySealTo", aliases: ["to", "seal to"] },
};

// Header names the backend recognises as the serial column, per item type (plus the
// generic ones accepted for every type).
const SERIAL_HEADERS_ANY = ["Serial No", "Identifier", "Barcode", "Asset No"];
const METER_SERIAL_HEADERS = ["Meter No", "Meter Number", "Meter Serial No", "Meter Barcode"];
export const SERIAL_HEADERS_BY_ITEM_TYPE = {
  NEW_METER: [...METER_SERIAL_HEADERS, ...SERIAL_HEADERS_ANY],
  OLD_METER: [...METER_SERIAL_HEADERS, ...SERIAL_HEADERS_ANY],
  MODEM: ["Modem No", "Modem Number", "Modem Serial No", "IMEI", ...SERIAL_HEADERS_ANY],
  SIM_CARD: ["SIM No", "SIM Number", "SIM Serial No", "ICCID", "MSISDN", ...SERIAL_HEADERS_ANY],
  SEAL: [],
};

// The identifier reads differently per item type, so its label is swapped here while
// the target sent to the backend stays `identifier`.
const IDENTIFIER_LABEL_KEY = {
  NEW_METER: "inventoryMeterNumber",
  OLD_METER: "inventoryMeterNumber",
  MODEM: "inventoryUploadFieldModemSerial",
  SIM_CARD: "inventoryUploadFieldSimSerial",
};

// The serial is the one main column; everything else is optional. SIM sheets carry
// no separate SIM No / ICCID fields since those headers *are* the SIM serial.
export const UPLOAD_FIELDS_BY_ITEM_TYPE = {
  NEW_METER: {
    mandatory: ["identifier"],
    optional: [
      "meterMake",
      "meterType",
      "meterComponents",
      "manufacturingYear",
      "batchNo",
      "simNo",
      "iccid",
    ],
  },
  OLD_METER: {
    mandatory: ["identifier"],
    optional: [
      "meterMake",
      "meterType",
      "meterComponents",
      "manufacturingYear",
      "batchNo",
    ],
  },
  MODEM: {
    mandatory: ["identifier"],
    optional: ["simNo", "iccid", "batchNo", "manufacturingYear"],
  },
  SIM_CARD: {
    mandatory: ["identifier"],
    optional: ["batchNo"],
  },
  // No serial column: From / To are the mandatory ones; the prefix is optional.
  SEAL: {
    mandatory: ["sealFrom", "sealTo"],
    optional: ["sealPrefix", "batchNo"],
  },
};

const knownType = (itemType) => (UPLOAD_FIELDS_BY_ITEM_TYPE[itemType] ? itemType : "NEW_METER");

const toField = (key, itemType, t) => {
  const labelKey = key === "identifier" ? IDENTIFIER_LABEL_KEY[itemType] : FIELD_DEFS[key].labelKey;
  return {
    key,
    label: t[labelKey] || t[FIELD_DEFS[key].labelKey] || key,
    aliases: key === "identifier" ? SERIAL_HEADERS_BY_ITEM_TYPE[itemType] : FIELD_DEFS[key].aliases,
  };
};

/** `{ mandatory: [{ key, label, aliases }], optional: [...] }` for the item type. */
export const uploadFieldsFor = (itemType, t) => {
  const type = knownType(itemType);
  const config = UPLOAD_FIELDS_BY_ITEM_TYPE[type];
  return {
    mandatory: config.mandatory.map((key) => toField(key, type, t)),
    optional: config.optional.map((key) => toField(key, type, t)),
  };
};

// Loose comparison for auto-matching and target clashes: "Meter Type", "meter_type"
// and "meterType" all compare equal, mirroring how the backend reads target names.
const normalize = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9؀-ۿ]/g, "");

// Strict comparison for serial detection -- only case and extra spaces are ignored,
// so the UI never claims a column is detected when the backend would not agree.
const normalizeHeader = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();

/** The sheet header the backend will pick as the serial column, or "" if none. */
export const detectSerialHeader = (itemType, headers = []) => {
  if (!Array.isArray(headers)) return "";
  const accepted = new Set(SERIAL_HEADERS_BY_ITEM_TYPE[knownType(itemType)].map(normalizeHeader));
  return headers.find((header) => accepted.has(normalizeHeader(header))) || "";
};

/**
 * Pre-fill a mapping by matching sheet headers against each field's key, translated
 * label and aliases. Fields already mapped in `current` (to a header that still
 * exists) are kept, and a header is never claimed by two fields.
 */
export const autoMatchColumns = (fields, headers, current = {}) => {
  const result = {};
  const used = new Set();
  fields.forEach((field) => {
    const kept = current[field.key];
    if (kept && headers.includes(kept) && !used.has(kept)) {
      result[field.key] = kept;
      used.add(kept);
    }
  });
  fields.forEach((field) => {
    if (result[field.key]) return;
    const candidates = new Set([field.key, field.label, ...field.aliases].map(normalize));
    const match = headers.find((header) => !used.has(header) && candidates.has(normalize(header)));
    if (match) {
      result[field.key] = match;
      used.add(match);
    }
  });
  return result;
};

// ---------------------------------------------------------------------------
// The mapping state the UI works with is `{ serial, targets }`:
// - `serial`  -- the sheet header holding the serial ("" = rely on detection).
// - `required` -- `{ fieldKey: header }` for mandatory fields other than the serial
//   (the From / To columns of a seal sheet).
// - `targets` -- `{ header: target }` for every other sheet column, where target is
//   an inventory field key or the header itself ("keep the column's own name").
// ---------------------------------------------------------------------------

/**
 * Every sheet column except the reserved ones (the serial, or a seal sheet's range
 * columns), in sheet order. Takes one header or a list.
 */
export const otherColumns = (headers, reserved) => {
  const skip = new Set([].concat(reserved).filter(Boolean));
  return headers.filter((header) => !skip.has(header));
};

/** `{ header: fieldKey }` for the non-serial mandatory columns (seal range columns). */
const requiredByHeader = (required = {}) =>
  Object.fromEntries(
    Object.entries(required || {})
      .filter(([, header]) => header)
      .map(([fieldKey, header]) => [header, fieldKey])
  );

/** The headers taken by the mandatory step: the serial plus any required columns. */
export const reservedHeaders = ({ serial, required }, itemType, headers) =>
  [serial || detectSerialHeader(itemType, headers), ...Object.values(required || {})].filter(Boolean);

/**
 * Default target for each non-serial column: the inventory field it auto-matches,
 * otherwise its own name. Choices already in `current` are kept.
 */
export const initialTargets = (optionalFields, headers, current = {}) => {
  const kept = Object.entries(current).filter(([header]) => headers.includes(header));
  const takenFields = new Set(kept.map(([, target]) => target));
  const fieldToHeader = autoMatchColumns(
    optionalFields.filter((field) => !takenFields.has(field.key)),
    headers.filter((header) => !current[header])
  );
  const targets = Object.fromEntries(kept);
  Object.entries(fieldToHeader).forEach(([fieldKey, header]) => {
    targets[header] = fieldKey;
  });
  headers.forEach((header) => {
    if (!targets[header]) targets[header] = header;
  });
  return targets;
};

/**
 * The target each sheet column ends up on, the way the backend resolves it: the
 * serial goes to `identifier`, a mapped column to its target, and anything else
 * keeps its own name.
 */
const effectiveTargets = ({ serial, required, targets = {} }, itemType, headers) => {
  const serialHeader = serial || detectSerialHeader(itemType, headers);
  const requiredTargets = requiredByHeader(required);
  return headers.map((header) => ({
    header,
    target:
      header === serialHeader ? "identifier" : requiredTargets[header] || targets[header] || header,
  }));
};

/**
 * Columns that would land on the same target -- the backend rejects the whole sheet
 * for this ("Columns 'Latitude' and 'Longitude' both map to 'longitude'"). Targets
 * compare loosely ("Meter Make" == meterMake), and a recognised serial header that
 * is not the chosen serial still counts as a second serial.
 *
 * @returns {{ header: string, other: string, target: string }[]}
 */
export const findTargetConflicts = (state, itemType, headers) => {
  // A column keeping its own name that is a recognised serial header is still
  // picked up by the backend as the serial.
  const keyOf = ({ header, target }) =>
    target === "identifier" || (target === header && detectSerialHeader(itemType, [header]))
      ? "identifier"
      : normalize(target);
  const seen = new Map();
  const conflicts = [];
  effectiveTargets(state, itemType, headers).forEach((entry) => {
    const key = keyOf(entry);
    const first = seen.get(key);
    if (first) conflicts.push({ header: first.header, other: entry.header, target: entry.target });
    else seen.set(key, entry);
  });
  return conflicts;
};

/**
 * The backend's `{ header: target }`. Every non-serial column is sent explicitly;
 * the serial is sent only when it is not the header the backend detects itself.
 */
export const toBackendColumnMappings = ({ serial, required, targets = {} }, itemType, headers) => {
  const detected = detectSerialHeader(itemType, headers);
  const result = {};
  if (serial && serial !== detected) result[serial] = "identifier";
  // Seal range columns are always sent, since "From" / "To" can be spelled many ways.
  Object.assign(result, requiredByHeader(required));
  otherColumns(headers, reservedHeaders({ serial, required }, itemType, headers)).forEach((header) => {
    result[header] = targets[header] || header;
  });
  return result;
};

/** A one-row sample sheet with the columns a seal upload expects. */
export const downloadSealTemplate = () => {
  const worksheet = XLSX.utils.aoa_to_sheet([
    ["Prefix", "From", "To", "Batch No"],
    ["SL", 1001, 2000, ""],
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Seals");
  XLSX.writeFile(workbook, "seal-upload-template.xlsx");
};

/**
 * Header row of the first sheet. Only the first row is parsed (`sheetRows: 1`), so a
 * large sheet is not fully decoded just to list its columns. Blank and duplicate
 * headers are dropped since they cannot be mapped unambiguously.
 */
export const readSheetHeaders = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(new Uint8Array(event.target.result), {
          type: "array",
          sheetRows: 1,
        });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!worksheet || !worksheet["!ref"]) {
          resolve([]);
          return;
        }
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        const headers = [];
        for (let col = range.s.c; col <= range.e.c; col += 1) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: range.s.r, c: col })];
          const header = cell?.v === undefined || cell?.v === null ? "" : String(cell.v);
          if (header.trim() && !headers.includes(header)) headers.push(header);
        }
        resolve(headers);
      } catch (error) {
        reject(error);
      }
    };
    reader.readAsArrayBuffer(file);
  });
